import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import type { FlightsClassTypes, FareClassWithReward } from 'constants/flight'
import type { MaldivesCabinHotelEntry } from '../hooks/useMaldivesOffersPricing'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import SolidSpinnerIcon from 'LuxKit/Icons/solid/SolidSpinnerIcon'
import MaldivesCharterFareCarouselOption, { type MaldivesCharterTripOption } from './MaldivesCharterFareCarouselOption'
import { orderMaldivesCharterFareClassesFullPresentation } from 'lib/maldivesCharter/orderMaldivesCharterTableFareClassesForSelectedFilter'

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

const CarouselWrap = styled.div`
  grid-column: 1 / -1;
  padding: ${rem(12)} 0 ${rem(8)} 0;
  background-color: #fff;
`

const FareCarouselShell = styled.div<{ $dimmed: boolean }>`
  position: relative;
  opacity: ${p => (p.$dimmed ? 0.48 : 1)};
  transition: opacity 0.2s ease;
  ${p => (p.$dimmed ? 'pointer-events: none;' : '')}
`

const FareCarouselSpinnerAnchor = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  display: flex;
  color: ${p => p.theme.palette.neutral.default.two};
`

const SpinningFareIcon = styled(SolidSpinnerIcon)`
  animation: ${spin} 0.75s linear infinite;
`

interface Props {
  fareClasses: ReadonlyArray<FareClassWithReward>
  selectedFareClass: FareClassWithReward
  /** When true, the matching fare card shows the chosen "Selected" treatment for this calendar date row. */
  isDateRowSelected: boolean
  checkIn: string
  checkOut: string
  nights: number
  rooms: Array<App.Occupants>
  priceMode: 'total' | 'perPerson'
  luxPlusEnabled: boolean
  tripOption: MaldivesCharterTripOption
  onPickFare: (fareClass: FareClassWithReward, option: MaldivesCharterTripOption) => void
  onUnselectSelectedFare?: () => void
  hotelPriceByCabinAndDate?: Partial<Record<FlightsClassTypes, Record<string, MaldivesCabinHotelEntry>>>
}

function MaldivesCharterDateFareCarousel(props: Props) {
  const {
    fareClasses,
    selectedFareClass,
    isDateRowSelected,
    checkIn,
    checkOut,
    nights,
    rooms,
    priceMode,
    luxPlusEnabled,
    tripOption,
    onPickFare,
    onUnselectSelectedFare,
    hotelPriceByCabinAndDate,
  } = props

  const intl = useIntl()
  const carouselRef = useRef<HTMLDivElement>(null)
  const previousSelectedFareRef = useRef<FareClassWithReward | null>(null)
  const [showFareSwapTransition, setShowFareSwapTransition] = useState(false)
  const [fareDetailsExpanded, setFareDetailsExpanded] = useState(false)
  const [extraFeaturesExpanded, setExtraFeaturesExpanded] = useState(false)

  const toggleFareDetails = useCallback(() => {
    setFareDetailsExpanded(v => !v)
  }, [])

  const toggleExtraFeatures = useCallback(() => {
    setExtraFeaturesExpanded(v => !v)
  }, [])

  const fareSwapSpinnerAriaLabel = intl.formatMessage({
    id: '8majp7K16JIT9Rfaq5Nb',
    defaultMessage: 'Updating fare options',
    description:
      'Short loading state announced to screen readers while the Maldives charter fare carousel scrolls back to show the newly selected cabin after the fare class filter changes.',
  })

  const orderedFareClasses = useMemo(() => {
    return orderMaldivesCharterFareClassesFullPresentation(fareClasses)
  }, [fareClasses])

  useLayoutEffect(() => {
    if (previousSelectedFareRef.current === null) {
      previousSelectedFareRef.current = selectedFareClass
      return
    }
    if (previousSelectedFareRef.current === selectedFareClass) {
      return
    }
    previousSelectedFareRef.current = selectedFareClass

    const el = carouselRef.current
    if (!el) {
      return
    }

    const hadScrolledAwayFromStart = el.scrollLeft > 16
    if (hadScrolledAwayFromStart) {
      setShowFareSwapTransition(true)
    }

    el.scrollTo({
      left: 0,
      behavior: hadScrolledAwayFromStart ? 'smooth' : 'auto',
    })

    if (!hadScrolledAwayFromStart) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      setShowFareSwapTransition(false)
    }, 340)

    return () => {
      window.clearTimeout(timeoutId)
      setShowFareSwapTransition(false)
    }
  }, [selectedFareClass])

  return (
    <CarouselWrap>
      <FareCarouselShell
        $dimmed={showFareSwapTransition}
        aria-busy={showFareSwapTransition}
      >
        {showFareSwapTransition && (
          <FareCarouselSpinnerAnchor role="status" aria-live="polite" aria-label={fareSwapSpinnerAriaLabel}>
            <SpinningFareIcon size="L" aria-hidden />
          </FareCarouselSpinnerAnchor>
        )}
        <CardCarousel
          ref={carouselRef}
          gap={12}
          snap="start"
          gutterSize={16}
          gutterStyle="overflow"
        >
          {orderedFareClasses.map((fc) => (
            <MaldivesCharterFareCarouselOption
              key={fc}
              optionFareClass={fc}
              checkIn={checkIn}
              checkOut={checkOut}
              nights={nights}
              rooms={rooms}
              priceMode={priceMode}
              luxPlusEnabled={luxPlusEnabled}
              isSelectedFare={isDateRowSelected && fc === selectedFareClass}
              tripOption={tripOption}
              onPickFare={onPickFare}
              onUnselectSelectedFare={onUnselectSelectedFare}
              fareDetailsExpanded={fareDetailsExpanded}
              extraFeaturesExpanded={extraFeaturesExpanded}
              onToggleFareDetails={toggleFareDetails}
              onToggleExtraFeatures={toggleExtraFeatures}
              hotelPriceByCabinAndDate={hotelPriceByCabinAndDate}
            />
          ))}
        </CardCarousel>
      </FareCarouselShell>
    </CarouselWrap>
  )
}

export default MaldivesCharterDateFareCarousel
